<script setup lang="ts">
import type { OrganizationJs } from "@/generated/models";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { useAxureCloudConfig } from "ls/state/useAxureCloudConfig";
import { CheckIcon } from "lucide-vue-next";
import { computed } from "vue";
import { useAccessLevelFromOrganization } from "../useOrganizationAccessLevel";
import { useOrganizationAdminInfo } from "../useOrganizationAdminInfo";
import { useSeatsAndLicensesInfo } from "../useSeatsAndLicensesInfo";
import { useSubInstanceSubscriptionsInfo } from "../useSubInstanceLicensingInfo";

const props = defineProps<{
  organization: OrganizationJs;
  isOnBillingPage?: boolean;
}>();

const subscriptionInfo = useSubInstanceSubscriptionsInfo(() => props.organization.Id);
const activeSubscriptions = computed(() => subscriptionInfo.value?.filter(subscription => !subscription.Subscription.IsCanceled));
const hasAdminManagedSubscription = computed(() => activeSubscriptions.value?.some(subscription => subscription.Subscription.IsAdminManaged));
const isLegacyOnly = computed(() => activeSubscriptions.value?.every(subscription => subscription.Subscription.IsLegacyAsecLicense));
const hasOwnedSubscription = computed(() => activeSubscriptions.value?.some(subscription => subscription.IsOwner));

const showPrimaryBuyButton = computed(() => {
  if (activeSubscriptions.value?.length === 0) return true;
  if (isLegacyOnly.value) return true;
  return false;
});

const { isAdmin } = useAccessLevelFromOrganization(() => props.organization);
const subInstanceAdminInfo = useOrganizationAdminInfo(() => props.organization.Id);

const seatsAndLicensesInfo = useSeatsAndLicensesInfo(() => props.organization.Id);

const config = useAxureCloudConfig();
const buyNewSubscriptionUrl = computed(() => {
  const portalUrl = config.value?.PortalUrl;

  const url = new URL("/buy", portalUrl);
  url.searchParams.set("type", "1");
  url.searchParams.set("domain", subInstanceAdminInfo.value?.Domain ?? "");

  if (seatsAndLicensesInfo.cloudAuthorUsersWithoutSeatCount.value
    && seatsAndLicensesInfo.cloudAuthorUsersWithoutSeatCount.value > 0) {
    url.searchParams.set("authorQty", `${seatsAndLicensesInfo.cloudAuthorUsersWithoutSeatCount.value}`);
  }

  return url.href;
});

const canAddSeats = computed(() => {
  if (hasAdminManagedSubscription.value && isAdmin.value) return true;
  if (hasOwnedSubscription.value) return true;
  return false;
});
</script>

<template>
  <div class="h-full max-w-58 flex-1 rounded-sm border border-slate-200 bg-white p-4">
    <div class="mb-4 flex">
      <label class="text-base font-semibold text-blue-600">
        Cloud Author seats
      </label>
    </div>
    <div class="mb-6 flex gap-2">
      <div class="flex text-3xl leading-none font-semibold whitespace-nowrap">
        $10
      </div>
      <div class="self-end leading-snug text-slate-500">
        author/month
      </div>
    </div>

    <div class="mb-12">
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Unlimited whiteboards
        </div>
      </div>
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Unlimited documents
        </div>
      </div>
      <div class="mb-2 flex items-start gap-2">
        <CheckIcon class="h-lh w-4 flex-none text-blue-600" aria-hidden="true" />
        <div>
          Unlimited templates
        </div>
      </div>
    </div>
    <template v-if="!props.isOnBillingPage || !isAdmin">
      <SimpleButton
        v-if="isAdmin"
        size="large"
        class="w-full"
        :href="buyNewSubscriptionUrl"
        target="_blank"
      >
        Buy Cloud Author seats
      </SimpleButton>
      <div v-else class="mb-4 rounded-sm border border-slate-300 p-4 text-center text-slate-500">
        Ask an admin to add Cloud Author seats
      </div>
    </template>
    <template v-else>
      <SimpleButton
        v-if="showPrimaryBuyButton"
        size="large"
        class="w-full"
        :href="buyNewSubscriptionUrl"
        target="_blank"
      >
        Buy Cloud Author seats
      </SimpleButton>
      <div v-else>
        <div class="flex justify-center">
          <p v-if="canAddSeats" class="mb-4 text-center">
            <span class="font-semibold">Add seats</span> to an existing subscription or
          </p>
          <p v-else class="mb-4 text-center">
            Ask the owner to add seats to an existing subscription or
          </p>
        </div>
        <SimpleButton
          size="large"
          class="w-full"
          :href="buyNewSubscriptionUrl"
          target="_blank"
          variant="secondary"
        >
          Buy new subscription
        </SimpleButton>
      </div>
    </template>
  </div>
</template>
